import type { ApplicationHost, GameData } from '../app/GameApplication';
import { KeyboardInput } from '../input/KeyboardInput';
import { FocusPauseGuard } from '../input/FocusPauseGuard';
import { VehicleRuntime, type CarStepResult } from './VehicleRuntime';
import { CAR_CONFIGS } from '../data/cars';
import { TRACK_CONFIGS } from '../data/tracks';
import { selectionState } from '../SelectionState';
import type { CarInputState } from '../types';
import { LapTimer } from '../../utils/LapTimer';
import { HudOverlay } from '../../ui/HudOverlay';
import {
  ApiClient,
  ApiError,
  type LapHistoryResponseDto,
  type LapHistoryRowDto,
  type RaceAttemptGrant,
  type TimingReferencesDto,
} from '../../api/client';
import {
  FRESH_TIRE_HEALTH,
  FixedTickAccumulator,
  InputRecorder,
  STEP_MS,
  AttemptAudit,
  type ReplayAttemptAudit,
  type ReplayEnvelopeV1,
  sectorTicksToMilliseconds,
  ticksToMilliseconds,
} from '@cars/shared';
import { CameraZoomController } from '../camera/CameraZoomController';
import { ViewportDebugOverlay } from '../camera/ViewportDebugOverlay';
import { readReducedCameraMotion, writeReducedCameraMotion } from '../camera/reducedCameraMotion';
import {
  classifyLapSubmissionError,
  lapSubmissionStatusMessage,
  LapSubmissionCoordinator,
  type LapSubmissionErrorDisposition,
  type LapSubmissionCandidate,
} from '../LapSubmissionCoordinator';
import {
  classifyGlobalGap,
  describeGlobalGap,
  formatSignedGapMs,
} from '../../utils/timingPresentation';
import { realtimeClient } from '../../realtime/instance';
import { ACTIVE_RULESET_ID, type TrackRecordDto } from '@cars/shared';
import { PauseOverlay } from '../../ui/PauseOverlay';

interface ActiveLapSubmission {
  candidate: LapSubmissionCandidate;
  grant: RaceAttemptGrant;
  replay: ReplayEnvelopeV1;
  audit: ReplayAttemptAudit | undefined;
}

export class LiveSession {
  private readonly host: ApplicationHost;
  private input: KeyboardInput | null = null;
  private focusGuard: FocusPauseGuard | null = null;
  private disposed = false;

  private vehicle: VehicleRuntime | null = null;
  private get car(): VehicleRuntime {
    if (!this.vehicle) throw new Error('Vehicle is not loaded');
    return this.vehicle;
  }
  private readonly lapTimer = new LapTimer();
  private readonly apiClient = new ApiClient('/api');
  private hudOverlay: HudOverlay | null = null;
  private selectedTrackId = 1;
  private selectedCarId = 1;
  private username = '';
  private lapSubmitInFlight = false;
  private readonly lapSubmissions = new LapSubmissionCoordinator();
  private activeLapSubmission: ActiveLapSubmission | null = null;
  private isPaused = false;
  private skipResumeFrame = false;
  private pauseOverlay: PauseOverlay | null = null;
  private completedLapCount = 0;
  private readonly sessionLaps: LapHistoryRowDto[] = [];
  private lapStartPitStopCount = 0;
  private readonly accumulator = new FixedTickAccumulator();
  private readonly cameraZoomController = new CameraZoomController();
  private viewportDebugOverlay: ViewportDebugOverlay | null = null;
  private trackBounds = { width: 0, height: 0 };
  private simulationTick = 0;
  private isReady = false;
  private replayRecorder: InputRecorder | null = null;
  private lastReplay: ReplayEnvelopeV1 | null = null;
  private attemptAudit: AttemptAudit | null = null;
  private attemptGrant: RaceAttemptGrant | null = null;
  private timingReferences: TimingReferencesDto | null = null;
  private sceneGeneration = 0;
  private removeRealtimeListener: (() => void) | null = null;
  private readonly handleFocusLoss = (): void => {
    if (!this.isReady || this.disposed || !this.host.isActive()) return;
    // Freeze at the current tick before another frame can step. This is an
    // ordinary audited pause, not a continuing run with interrupted input.
    this.input?.clear();
    if (!this.isPaused) this.togglePause();
  };

  constructor(host: ApplicationHost) {
    this.host = host;
  }

  async create(data: GameData): Promise<void> {
    this.sceneGeneration += 1;
    this.isReady = false;
    this.attemptGrant = null;
    this.timingReferences = null;
    this.replayRecorder = null;
    this.attemptAudit = null;
    this.accumulator.reset();
    this.cameraZoomController.reset();
    this.simulationTick = 0;
    this.lapTimer.reset();
    this.lapSubmitInFlight = false;
    this.lapSubmissions.clear();
    this.activeLapSubmission = null;
    this.completedLapCount = 0;
    this.sessionLaps.length = 0;
    this.lapStartPitStopCount = 0;
    this.isPaused = false;
    this.skipResumeFrame = false;
    this.pauseOverlay?.destroy();
    this.pauseOverlay = null;
    const fallback = selectionState.get();
    const trackId = data.trackId ?? fallback.trackId;
    const carId = data.carId ?? fallback.carId;
    this.username = data.username?.trim() ?? '';

    const trackConfig = TRACK_CONFIGS[trackId];
    const carConfig = CAR_CONFIGS[carId];

    if (!trackConfig || !carConfig) {
      throw new Error('Missing game config for selected car/track');
    }

    selectionState.set({ carId: carConfig.id, trackId: trackConfig.id });
    this.selectedTrackId = trackConfig.id;
    this.selectedCarId = carConfig.id;

    const assets = await this.host.assets.loadRace(trackConfig, carConfig, this.host.signal, {
      maxTextureSize: this.host.renderer.maxTextureSize,
    });
    if (!this.host.isActive()) {
      assets.scene?.model.dispose();
      return;
    }
    this.trackBounds = {
      width: assets.landscape.naturalWidth,
      height: assets.landscape.naturalHeight,
    };
    this.host.renderer.load(assets);
    this.vehicle = new VehicleRuntime(
      trackConfig.startPointX,
      trackConfig.startPointY,
      trackConfig.startPointRotation,
      assets.track,
      carConfig,
    );
    this.car.setTimer(this.lapTimer);
    this.host.renderer.present(this.car.getInterpolatedPose(0));
    const runMetadata = this.car.getRunMetadata();
    try {
      this.attemptGrant = await this.apiClient.createRaceAttempt(trackConfig.id, carConfig.id);
      if (!this.host.isActive()) return;
      this.timingReferences = this.attemptGrant.timingReferences;
      if (
        this.attemptGrant.trackMapHash !== runMetadata.trackMapHash ||
        this.attemptGrant.carConfigHash !== runMetadata.carConfigHash
      ) {
        throw new Error('Server race content does not match this client');
      }
    } catch (error) {
      if (!this.host.isActive()) return;
      console.warn('Unable to create a secure race attempt', error);
      this.host.showMenu({
        username: this.username,
        showScoreboard: true,
        startupError: 'Unable to start a verified race right now. Please try again.',
      });
      return;
    }
    this.attemptAudit = new AttemptAudit(this.attemptGrant.attemptId);
    this.replayRecorder = new InputRecorder({
      trackId: trackConfig.id,
      carId: carConfig.id,
      trackMapHash: this.attemptGrant.trackMapHash,
      carConfigHash: this.attemptGrant.carConfigHash,
      rulesetId: this.attemptGrant.rulesetId,
      attemptId: this.attemptGrant.attemptId,
      startTick: 1,
      initialState: this.car.getState(),
    });
    this.lastReplay = null;

    this.input = new KeyboardInput();
    this.cameraZoomController.setReducedMotion(readReducedCameraMotion());
    this.resize();
    this.host.uiRoot.replaceChildren();
    const uiLayer = this.host.uiRoot;
    if (!uiLayer) {
      throw new Error('Missing #ui-layer element');
    }

    this.hudOverlay = new HudOverlay(uiLayer, trackConfig, this.trackBounds.width);
    this.removeRealtimeListener = realtimeClient.on((event) => {
      if (event.type === 'record-updated') this.applyGlobalRecord(event.record);
      if (event.type === 'snapshot')
        for (const record of event.records) this.applyGlobalRecord(record);
    });
    this.pauseOverlay = new PauseOverlay(uiLayer, {
      trackId: this.selectedTrackId,
      rulesetId: ACTIVE_RULESET_ID,
      loadHistory: async () => this.getSessionLapHistory(),
      onRestartVerifiedRun: () => {
        this.host.startRace({
          trackId: this.selectedTrackId,
          carId: this.selectedCarId,
          username: this.username,
        });
      },
      onResume: () => this.resumeRun(),
      onReturnToMain: () => {
        this.host.showMenu({ username: this.username, showScoreboard: true });
      },
      onReducedMotionChange: (enabled) => {
        this.cameraZoomController.setReducedMotion(enabled);
        writeReducedCameraMotion(enabled);
        this.host.renderer.setZoom(this.cameraZoomController.getZoom());
        this.host.renderer.redraw();
        this.updateViewportDebugOverlay();
      },
      reducedMotion: this.cameraZoomController.isReducedMotion(),
    });
    if (import.meta.env.VITE_VIEWPORT_DEBUG === 'true') {
      this.viewportDebugOverlay = new ViewportDebugOverlay(uiLayer, () =>
        this.host.renderer.renderer.forceContextLoss(),
      );
      this.updateViewportDebugOverlay();
    }
    this.isReady = true;
    const currentRecord = realtimeClient.getCurrentRecord(this.selectedTrackId, ACTIVE_RULESET_ID);
    if (currentRecord) this.applyGlobalRecord(currentRecord);
    this.host.resetFrameClock();
    this.focusGuard = new FocusPauseGuard(
      {
        windowEvents: window,
        documentEvents: document,
        isVisible: () => document.visibilityState === 'visible',
        hasFocus: () => document.hasFocus(),
      },
      this.handleFocusLoss,
    );
    this.focusGuard.check();
  }

  getLastReplay(): ReplayEnvelopeV1 | null {
    return this.lastReplay;
  }

  private applyGlobalRecord(record: TrackRecordDto): void {
    if (
      !this.isReady ||
      record.trackId !== this.selectedTrackId ||
      record.rulesetId !== ACTIVE_RULESET_ID
    )
      return;
    const current = this.timingReferences;
    if (current && record.revision <= current.revision) return;
    this.timingReferences = {
      trackId: record.trackId,
      rulesetId: record.rulesetId,
      revision: record.revision,
      globalRecord: {
        timeMs: record.timeMs,
        sectorTimesMs: record.sectorTimesMs,
        replayId: record.replayId,
        carId: record.carId,
      },
      personalBest: current?.personalBest ?? null,
    };
    this.pauseOverlay?.updateReferences(
      record.timeMs,
      this.timingReferences.personalBest?.timeMs ?? null,
      record.revision,
    );
  }

  getAttemptAudit(): ReplayAttemptAudit | null {
    return this.attemptAudit?.snapshot(this.attemptGrant?.attemptId) ?? null;
  }

  update(delta: number): void {
    if (!this.isReady) return;
    this.focusGuard?.check();
    if (this.input?.takeEscape()) {
      this.togglePause();
    }

    if (this.isPaused) return;
    // The first render delta after resuming may include time spent paused.
    if (this.skipResumeFrame) {
      this.skipResumeFrame = false;
      return;
    }

    this.host.renderer.recordFrame(delta);
    let result: CarStepResult = { lapFinished: false };
    const accumulatorResult = this.accumulator.advance(delta, () => {
      const input: CarInputState = this.input?.read() ?? {
        accelerate: false,
        brake: false,
        turnLeft: false,
        turnRight: false,
      };
      this.simulationTick += 1;
      const tickResult = this.car.stepOnce(STEP_MS, input, this.simulationTick);
      if (tickResult.sectorIndex !== undefined && tickResult.sectorTicks !== undefined) {
        this.showSectorTiming(tickResult.sectorIndex);
      }
      if (tickResult.controls && tickResult.state) {
        try {
          this.replayRecorder?.recordTick(
            this.simulationTick,
            tickResult.controls,
            tickResult.state,
          );
          if (typeof tickResult.checkpointIndex === 'number') {
            this.replayRecorder?.recordCheckpoint(tickResult.checkpointIndex, this.simulationTick);
          }
        } catch (error) {
          console.warn('Replay recording stopped', error);
          this.replayRecorder = null;
        }
      }
      if (tickResult.lapFinished && !result.lapFinished) result = tickResult;
    });
    if (accumulatorResult.invalidated) {
      this.car.markAttemptInvalid();
      throw new Error('The run stopped after a long frame. Restart the verified run to continue.');
    }
    const pose = this.car.getInterpolatedPose(accumulatorResult.alpha);
    const telemetry = this.car.getTelemetry();
    this.cameraZoomController.update(delta, telemetry.speedRatio, telemetry.wallHit);
    this.host.renderer.setZoom(this.cameraZoomController.getZoom());
    this.host.renderer.present(pose, true, this.car.getPathTrace());
    this.updateViewportDebugOverlay();
    this.hudOverlay?.setCurrentTime(
      this.lapTimer.getCurrentTimeAtSimulationTick(this.simulationTick),
    );
    this.hudOverlay?.setSpeed(telemetry.speedRatio);
    this.hudOverlay?.setTireHealth(this.car.getState().tires ?? FRESH_TIRE_HEALTH);
    this.hudOverlay?.setZoneStatus(
      telemetry.pitSpeedLimitActive ?? false,
      telemetry.pitServiceActive ?? false,
      telemetry.pitServiceVisit ?? false,
    );
    this.hudOverlay?.setMinimapCarPoint(pose.x, pose.y);
    if (result.lapFinished && !accumulatorResult.invalidated && !this.car.isAttemptInvalid()) {
      this.hudOverlay?.setLastTime(this.lapTimer.getLastLapTime());
      this.hudOverlay?.setBestTime(this.lapTimer.getBestLapTime());
      if (typeof result.lapTimeMs === 'number' && result.lapTimeMs > 0 && this.replayRecorder) {
        try {
          const replay = this.replayRecorder.snapshot(this.attemptGrant?.attemptId);
          this.lastReplay = replay;
          this.completedLapCount += 1;
          const currentPitStopCount = result.state?.pitStopVisitCount ?? this.lapStartPitStopCount;
          const pitStopCount = Math.max(0, currentPitStopCount - this.lapStartPitStopCount);
          this.lapStartPitStopCount = currentPitStopCount;
          if (result.lapSectorTicks) {
            this.sessionLaps.push({
              replayId: `session-lap-${this.completedLapCount}`,
              carId: this.selectedCarId,
              timeMs: Math.round(result.lapTimeMs),
              sectorTimesMs: sectorTicksToMilliseconds(
                result.lapSectorTicks,
                this.attemptGrant?.tickRate ?? 60,
              ),
              visitedPitStop: pitStopCount > 0,
              pitStopCount,
              completedAt: new Date().toISOString(),
            });
            this.pauseOverlay?.invalidate();
          }
          this.queueLapSubmission(result.lapTimeMs, replay, this.attemptAudit?.snapshot());
        } catch (error) {
          console.warn('Replay finalization failed', error);
        }
      }
    }
  }

  private togglePause(): void {
    if (!this.isPaused) {
      this.isPaused = true;
      this.attemptAudit?.record('pause', this.simulationTick);
    } else {
      this.resumeRun();
      return;
    }
    this.pauseOverlay?.setVisible(this.isPaused);
  }

  private resumeRun(): void {
    if (!this.isPaused) return;
    this.attemptAudit?.record('resume', this.simulationTick);
    this.skipResumeFrame = true;
    this.host.resetFrameClock();
    this.pauseOverlay?.setVisible(false);
    this.isPaused = false;
  }

  private queueLapSubmission(
    lapTimeMs: number,
    replay?: ReplayEnvelopeV1,
    audit?: ReplayAttemptAudit,
  ): void {
    try {
      this.lapSubmissions.queue({
        claimedTimeMs: Math.round(lapTimeMs),
        replay,
        audit,
        lapNumber: this.completedLapCount,
      });
    } catch (error) {
      console.warn('Lap submission queue is full', error);
      this.hudOverlay?.showErrorMessage(
        'Lap saving queue is full; finish fewer laps before retrying.',
      );
      return;
    }
    void this.flushLapSubmission();
  }

  private async flushLapSubmission(): Promise<void> {
    if (this.lapSubmitInFlight || !this.replayRecorder) return;
    this.lapSubmitInFlight = true;
    if (!this.activeLapSubmission) {
      const candidate = this.lapSubmissions.take();
      if (!candidate) {
        this.lapSubmitInFlight = false;
        return;
      }
      const grant = this.attemptGrant ?? (await this.issueNextAttempt());
      if (!this.host.isActive()) return;
      if (!grant) {
        this.lapSubmissions.restore(candidate);
        this.lapSubmitInFlight = false;
        return;
      }
      const replay = {
        ...(candidate.replay ?? this.replayRecorder.snapshot(grant.attemptId)),
        attemptId: grant.attemptId,
        rulesetId: grant.rulesetId,
      };
      this.lastReplay = replay;
      this.activeLapSubmission = {
        candidate,
        grant,
        replay,
        audit: candidate.audit
          ? { ...candidate.audit, attemptId: grant.attemptId }
          : this.attemptAudit?.snapshot(grant.attemptId),
      };
    }

    const active = this.activeLapSubmission;
    if (!active) {
      this.lapSubmitInFlight = false;
      return;
    }
    let submissionCommitted = false;
    let terminal = false;
    let disposition: LapSubmissionErrorDisposition = 'retryable';
    try {
      await this.apiClient.submitRaceAttempt(active.grant.attemptId, {
        challenge: active.grant.challenge,
        replay: active.replay,
        claimedTimeMs: active.candidate.claimedTimeMs,
        audit: active.audit,
        lapNumber: active.candidate.lapNumber,
      });
      if (!this.host.isActive()) return;
      submissionCommitted = true;
      terminal = true;
      this.activeLapSubmission = null;
      this.lapSubmissions.settle(active.candidate);

      // The finish-line sector already presented the completed-lap gap. The
      // server acknowledgement updates references without showing it twice.
    } catch (error) {
      if (!this.host.isActive()) return;
      console.warn('Failed to submit verified lap', error);
      if (!submissionCommitted) {
        disposition =
          error instanceof ApiError
            ? classifyLapSubmissionError(error.status, error.code)
            : 'retryable';
        terminal = disposition !== 'retryable';
        if (disposition === 'grant-terminal') {
          this.attemptGrant = null;
          this.activeLapSubmission = null;
          this.lapSubmissions.restore(active.candidate);
        } else if (disposition === 'replay-terminal') {
          this.activeLapSubmission = null;
          this.lapSubmissions.settle(active.candidate);
        }
      }
      const code = error instanceof ApiError ? error.code : undefined;
      const message = lapSubmissionStatusMessage(disposition, code);
      if (disposition === 'replay-terminal') this.hudOverlay?.showErrorMessage(message);
      else this.hudOverlay?.showStatusMessage(message);
    } finally {
      if (terminal && this.isReady) {
        await this.issueNextAttempt();
      }
      this.lapSubmitInFlight = false;
      if (this.host.isActive() && !this.activeLapSubmission && this.lapSubmissions.hasPending()) {
        void this.flushLapSubmission();
      }
    }
  }

  private async issueNextAttempt(): Promise<RaceAttemptGrant | null> {
    const generation = this.sceneGeneration;
    this.attemptGrant = null;
    try {
      const grant = await this.apiClient.createRaceAttempt(
        this.selectedTrackId,
        this.selectedCarId,
      );
      if (!this.isReady || generation !== this.sceneGeneration) return null;
      const runMetadata = this.car.getRunMetadata();
      if (
        grant.trackMapHash !== runMetadata.trackMapHash ||
        grant.carConfigHash !== runMetadata.carConfigHash
      ) {
        throw new Error('Server race content does not match this client');
      }
      this.attemptGrant = grant;
      this.timingReferences = grant.timingReferences;
      this.pauseOverlay?.updateReferences(
        grant.timingReferences.globalRecord?.timeMs ?? null,
        grant.timingReferences.personalBest?.timeMs ?? null,
        grant.timingReferences.revision,
      );
      if (!this.attemptAudit) this.attemptAudit = new AttemptAudit(grant.attemptId);
      return grant;
    } catch (error) {
      if (!this.isReady || generation !== this.sceneGeneration) return null;
      console.warn('Unable to renew a secure race attempt', error);
      this.hudOverlay?.showErrorMessage('Verified lap saving is temporarily unavailable.');
      return null;
    }
  }

  private showSectorTiming(sectorIndex: 0 | 1 | 2): void {
    const references = this.timingReferences;
    const completedSectorTicks = this.lapTimer.getLastSectorTicks();
    const currentMs = ticksToMilliseconds(
      completedSectorTicks.slice(0, sectorIndex + 1).reduce((total, ticks) => total + ticks, 0),
      this.attemptGrant?.tickRate ?? 60,
    );
    // Capture primitive values at the boundary. A later record update may
    // replace the reference snapshot, but must never recolor this message.
    const globalRecord = references?.globalRecord;
    if (!globalRecord) return;
    const globalMs =
      sectorIndex === 2
        ? globalRecord.timeMs
        : globalRecord.sectorTimesMs
            .slice(0, sectorIndex + 1)
            .reduce((total, timeMs) => total + timeMs, 0);
    const gap = currentMs - globalMs;
    this.hudOverlay?.showTimingMessage(
      formatSignedGapMs(gap),
      classifyGlobalGap(gap),
      describeGlobalGap(gap, sectorIndex + 1),
    );
  }

  private getSessionLapHistory(): LapHistoryResponseDto {
    return {
      trackId: this.selectedTrackId,
      rulesetId: ACTIVE_RULESET_ID,
      recordRevision: this.timingReferences?.revision ?? 0,
      globalBestMs: this.timingReferences?.globalRecord?.timeMs ?? null,
      personalBestMs: this.timingReferences?.personalBest?.timeMs ?? null,
      laps: [...this.sessionLaps],
    };
  }

  resize(): void {
    const size = this.host.renderer.getSize();
    this.cameraZoomController.setViewport(size, this.trackBounds);
    this.host.renderer.setZoom(this.cameraZoomController.getZoom());
    this.host.renderer.redraw();
    this.updateViewportDebugOverlay();
  }

  private updateViewportDebugOverlay(): void {
    this.viewportDebugOverlay?.update(this.host.renderer.getMetrics());
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.sceneGeneration += 1;
    this.isReady = false;
    this.vehicle?.markAttemptInvalid();
    this.attemptAudit?.record('abandonment', this.simulationTick);
    this.input?.dispose();
    this.input = null;
    this.focusGuard?.dispose();
    this.focusGuard = null;
    this.removeRealtimeListener?.();
    this.removeRealtimeListener = null;
    this.hudOverlay?.destroy();
    this.hudOverlay = null;
    this.pauseOverlay?.destroy();
    this.pauseOverlay = null;
    this.viewportDebugOverlay?.destroy();
    this.viewportDebugOverlay = null;
    if (this.replayRecorder) {
      try {
        this.lastReplay = this.replayRecorder.finalize();
      } catch {
        this.lastReplay = null;
      }
    }
    this.replayRecorder = null;
    this.attemptGrant = null;
    this.lapSubmissions.clear();
    this.activeLapSubmission = null;
  }
}
